import React, { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
// @mui material components
import Grid from "@mui/material/Grid";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import AddNewPaymentForm from "examples/AddNewPaymentForm";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import LeasesService from "services/leases-service";
import LeaseInvoiceService from "services/lease-invoice-service";
import PaymentsService from "services/payments-service";

function AddNewPayment(props) {
  const { open, setOpen, onClose } = props; // Add onClose prop
  const [loadingButton, setLoadingButton] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null); // State for success message
  const [tenantCode, setTenantCode] = useState(null);
  const [tenantCodeError, setTenantCodeError] = useState(false);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState(""); // New state for keyword search
  const [loadingOptions, setLoadingOptions] = useState(false); // Loader for tenant options
  const [searchTimeout, setSearchTimeout] = useState(null); // Timeout for delaying search
  const [selectedLeaseDetails, setSelectedLeaseDetails] = useState(null); // State for selected lease details
  const [paymentForOptions, setPaymentForOptions] = useState([]); // State for "Being Payment For" options
  const [paymentFor, setPaymentFor] = useState(null); // State for selected "Being Payment For"
  const [paymentForError, setPaymentForError] = useState(false); // Error state for "Being Payment For"
  const [paymentForError2, setPaymentForError2] = useState(false); // Error state for "Being Payment For"
  const [amount, setAmount] = useState(""); // State for the Amount field
  const [amountError, setAmountError] = useState(false); // Error state for Amount field
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]); // Default to today's date
  const [paymentDateError, setPaymentDateError] = useState(false); // Error state for Payment Date
  const [paymentMethod, setPaymentMethod] = useState(""); // State for Payment Method
  const [paymentMethodError, setPaymentMethodError] = useState(false); // Error state for Payment Method
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]); // State for payment method options
  const [paidBy, setPaidBy] = useState(""); // State for Paid By field
  const [paidByError, setPaidByError] = useState(false); // Error state for Paid By field
  const [chequeNumber, setChequeNumber] = useState(""); // State for Cheque Number field
  const [chequeNumberError, setChequeNumberError] = useState(false); // Error state for Cheque Number field
  const [paymentDocument, setPaymentDocument] = useState(null); // State for Payment Document
  const [paymentDocumentError, setPaymentDocumentError] = useState(false); // Error state for Payment Document
  const [extraNotes, setExtraNotes] = useState(""); // State for Extra Notes

  const MAX_FILE_SIZE_MB = 8; // Maximum file size in MB

  useEffect(() => {
    const fetchTenantOptions = async (filter = "") => {
      setLoadingOptions(true);
      try {
        const data = await LeasesService.getList({
          limit: 10,
          page: 1,
          sortField: "lease_code",
          sortDirection: "desc",
          filter: filter
        });
        const options = data.data.map(item => ({
          label: item.lease_code,
          value: item.lease_code,
          details: item // Include full lease details in the options
        }));
        setTenantOptions(options);
      } catch (error) {
        console.error("Failed to fetch tenant options:", error);
      } finally {
        setLoadingOptions(false);
      }
    };

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      fetchTenantOptions(searchKeyword); // Pass the search keyword to the filter
    }, 500); // Delay of 500ms

    setSearchTimeout(timeout);
  }, [searchKeyword]); // Re-fetch options when the keyword changes

  useEffect(() => {
    const fetchPaymentForOptions = async () => {
      if (!tenantCode) return; // Only fetch options if tenantCode is selected

      try {
        const data = await LeaseInvoiceService.getInvoiceList({
          limit: 10,
          page: 1,
          whereField: 'lease_id',
          whereValue: selectedLeaseDetails.lease_id // Use the selected tenantCode value
        });

        const options = data.data.map(item => {
          let label = item.invoice_prop_number;
          if (item.remarks && item.remarks.includes("|")) {
            const [start_date, end_date] = item.remarks.split("|");
            label += ` | ${start_date} - ${end_date}`;
          } else {
            let invoice_date = item.invoice_date; // e.g. 01/06/2025
            let $month_number = invoice_date.split("/")[1]; // Extract month number
            let $year = invoice_date.split("/")[2]; // Extract year
            let start_date_of_month = `01/${$month_number}/${$year}`; // Start of month
            let end_date_of_month = new Date($year, $month_number, 0).getDate(); // Get last day of month
            end_date_of_month = `${end_date_of_month}/${$month_number}/${$year}`; // Format end date
            label += ` | ${start_date_of_month} - ${end_date_of_month}`; // Append formatted dates
          }
          if( ! item.summary?.amount_due_number ) {
            label += " (Invoice Paid)";
          }
          return {
            label: label,
            value: item.invoice_prop_number,
            details: item, // Include full invoice details in the options
            disabled: !item.summary?.amount_due_number // Disable option if amount_due_number is 0
          };
        });
        setPaymentForOptions(options);
      } catch (error) {
        console.error("Failed to fetch payment options:", error);
      }
    };

    const fetchPaymentMethodOptions = async () => {
      try {
        const data = await PaymentsService.fetchPaymentMethods({
          'list': 'display_name' // Fetch only display_name for options
        });
        setPaymentMethodOptions(data.map(method => ({
          label: method.display_name,
          value: method.id
        })));
        // Then set the first option for paymentMethod if available
        if (data.length > 0) {
          setPaymentMethod(data[0].id); // Set default payment method to the first option
        }
      } catch (error) {
        console.error("Failed to fetch payment method options");
      }
    };

    fetchPaymentForOptions();
    fetchPaymentMethodOptions(); // Trigger fetching payment methods when tenant code changes
  }, [tenantCode]); // Re-fetch options when tenantCode changes

  const handleTenantChange = (event, value) => {
    setTenantCode(value);
    setTenantCodeError(false); // Clear error when a valid option is selected
    setPaymentFor(null); // Reset "Being Payment For" field
    setPaymentForOptions([]); // Clear options for "Being Payment For"

    if (value) {
      const leaseDetails = tenantOptions.find(option => option.value === value.value); // Find lease details from tenantOptions
      const leaseDetailsData = leaseDetails ? {
        lease_id: leaseDetails.details.id,
        property_id: leaseDetails.details.property?.id,
        tenant_id: leaseDetails.details.tenants[0]?.id,
        lease_number: leaseDetails.details.lease_number,
        unit_names: leaseDetails.details.unit_names,
        first_name: leaseDetails.details.tenants[0]?.first_name,
        agreement_doc: leaseDetails.details.agreement_doc,
        property_name: leaseDetails.details.property?.property_name
      } : null;
      
      setSelectedLeaseDetails(leaseDetailsData || null); // Update state with found lease details
    } else {
      setSelectedLeaseDetails(null); // Clear details if no tenant code is selected
      setSearchKeyword(""); // Refresh tenant code data
    }
  };

  const handlePaymentForChange = (event, value) => {
    if( ! value?.details?.summary?.amount_due_number ) {
      alert('The selected invoice has already been paid. Please select another invoice.');
      setAmount(""); // Clear Amount field if no amount is available
      return; // Exit early if no valid amount is found
    }
    setPaymentForError2(false)
    setPaymentFor(value);
    setPaymentForError(false); // Clear error when a valid option is selected
    setAmount(value?.details?.summary?.amount_due_number || ""); // Load rent_amount into Amount field
  };

  const handleAmountChange = (event) => {
    const value = event.target.value;
    if (!isNaN(value)) {
      setAmount(value); // Update Amount field only if input is a number
      setAmountError(false); // Clear error
    } else {
      setAmountError(true); // Set error if input is not a number
    }
  };

  const handlePaymentDateChange = (event) => {
    setPaymentDate(event.target.value);
    setPaymentDateError(false); // Clear error
  };

  const handlePaymentMethodChange = (event) => {
    setPaymentMethod(event.target.value);
    setPaymentMethodError(false); // Clear error
  };

  const handlePaidByChange = (event) => {
    setPaidBy(event.target.value);
    setPaidByError(false); // Clear error
  };

  const handleChequeNumberChange = (event) => {
    setChequeNumber(event.target.value);
    setChequeNumberError(false); // Clear error
  };

  const handlePaymentDocumentChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
      ];
      const fileSizeMB = file.size / (1024 * 1024); // Convert file size to MB
      if (allowedTypes.includes(file.type) && fileSizeMB <= MAX_FILE_SIZE_MB) {
        setPaymentDocument(file);
        setPaymentDocumentError(false); // Clear error
      } else {
        setPaymentDocumentError(true); // Set error for unsupported file type or size
      }
    } else {
      setPaymentDocument(null); // Clear file if no file is selected
    }
  };

  const handleExtraNotesChange = (event) => {
    setExtraNotes(event.target.value); // Update Extra Notes field
  };

  const onHandleSubmit = async (event) => {
    event.preventDefault();
    if (!tenantCode) {
      setTenantCodeError(true); // Set error if tenantCode is empty
      return;
    }
    if (!paymentFor) {
      setPaymentForError(true); // Set error if paymentFor is empty
      return;
    }
    if (!amount) {
      setAmountError(true); // Set error if Amount is empty
      return;
    }
    if (!paymentDate) {
      setPaymentDateError(true); // Set error if Payment Date is empty
      return;
    }
    if (!paymentMethod) {
      setPaymentMethodError(true); // Set error if Payment Method is empty
      return;
    }
    if (!paidBy) {
      setPaidByError(true); // Set error if Paid By is empty
      return;
    }
    if (paymentMethod === "79039c7b-bb79-4876-907b-e9a496a06426" && !chequeNumber) {
      setChequeNumberError(true); // Set error if Cheque Number is empty
      return;
    }
    if (paymentDocumentError) {
      return; // Prevent submission if file type is invalid
    }

    setLoadingButton(true);
    try {
      const paymentData = {
        tenant_id: selectedLeaseDetails.tenant_id, 
        property_id: selectedLeaseDetails.property_id,
        lease_id: selectedLeaseDetails.lease_id,
        lease_number: selectedLeaseDetails.lease_number, 
        being_payment_for: paymentFor.value,
        amount: parseFloat(amount),
        payment_date: paymentDate,
        payment_method_id: paymentMethod,
        paid_by: paidBy,
        reference_number: paymentMethod === "79039c7b-bb79-4876-907b-e9a496a06426" ? chequeNumber : null,
        notes: extraNotes, 
        payment_document: paymentDocument, // File upload
      };

      const response = await PaymentsService.newPayment(paymentData);
      // if (response.status !== 200) {
      //   throw new Error("Failed to submit payment");
      // }
      if (!response.error) {
        setSuccessMessage("Payment submitted successfully!");
        setOpen(false); // Close the dialog
        if (onClose) onClose(); // Trigger the callback
      } else {
        setError(`Failed to submit payment: ${response.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Failed to submit payment:", error);
      setError(error.message);
    } finally {
      setLoadingButton(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null); // Clear the error after 4 seconds
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null); // Clear the success message after 4 seconds
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <Dialog open={open} scroll='paper' onClose={handleClose} aria-labelledby="scroll-dialog-title" fullWidth={true} disableScrollLock={false} maxWidth='md'>
      <DialogTitle id="scroll-dialog-title">
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Icon>close</Icon>
        </IconButton>
        <MDBox
          mx={2}
          py={3}
          px={2}
          variant="gradient"
          bgColor="success"
          borderRadius="lg"
          coloredShadow="info"
          sx={{ marginTop: '30px' }}
        >
          <MDTypography variant="h6" color="white">
            Add New Payment
          </MDTypography>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        {/* Tenant Code Field */}
        <Autocomplete
          options={tenantOptions}
          getOptionLabel={(option) => option.label}
          value={tenantCode}
          onChange={handleTenantChange}
          loading={loadingOptions} // Show loader while fetching options
          sx={{ paddingTop: '20px' }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tenant Code"
              variant="outlined"
              error={tenantCodeError}
              helperText={tenantCodeError ? "Tenant Code is required" : ""}
              required
              onChange={(e) => setSearchKeyword(e.target.value)} // Update search keyword
            />
          )}
        />
        {/* Being Payment For Field - Only visible when tenant code is selected */}
        {tenantCode && (
          <Autocomplete
            options={paymentForOptions}
            getOptionLabel={(option) => option.label}
            value={paymentFor}
            onChange={handlePaymentForChange}
            sx={{ paddingTop: '20px' }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Being Payment For"
                variant="outlined"
                error={paymentForError || paymentForError2}
                helperText={paymentForError2 ? "The selected invoice has already been paid. Please select another invoice." : paymentForError ? "This field is required" : ""}
                required
              />
            )}
          />
        )}
        
        {/* Display selected lease details */}
        {selectedLeaseDetails && (
          <MDBox mt={2}>
            <MDTypography variant="body2">
              Lease Details:
            </MDTypography>
            <MDBox component="ul" sx={{ paddingLeft: 2 }}>
              <MDBox component="li" display="flex" alignItems="center">
                <Icon sx={{ marginRight: 1 }}>apartment</Icon>
                <MDTypography variant="body2">
                  Property Name: <strong>{selectedLeaseDetails.property_name || "N/A"}</strong>
                </MDTypography>
              </MDBox>
              <MDBox component="li" display="flex" alignItems="center">
                <Icon sx={{ marginRight: 1 }}>apartment</Icon>
                <MDTypography variant="body2">
                  Lease Unit: <strong>{selectedLeaseDetails.unit_names || "N/A"}</strong>
                </MDTypography>
              </MDBox>
              <MDBox component="li" display="flex" alignItems="center">
                <Icon sx={{ marginRight: 1 }}>location_on</Icon>
                <MDTypography variant="body2">
                  Lease Address: <strong>{selectedLeaseDetails.agreement_doc || "N/A"}</strong>
                </MDTypography>
              </MDBox>
              <MDBox component="li" display="flex" alignItems="center">
                <Icon sx={{ marginRight: 1 }}>person</Icon>
                <MDTypography variant="body2">
                  Tenant Name: <strong>{selectedLeaseDetails.first_name || "N/A"}</strong>
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        )}

        {/* Combine Amount, Payment Date, and Payment Method fields into one row */}
        {paymentFor && (
          <MDBox mt={2}>
            <Grid container spacing={2}>
              {/* Amount Field */}
              <Grid item xs={4}>
                <TextField
                  label="Amount"
                  variant="outlined"
                  fullWidth
                  value={amount}
                  onChange={handleAmountChange}
                  error={amountError}
                  helperText={amountError ? "Amount is required and must be a number" : ""}
                  required
                  type="number"
                  InputProps={{
                    readOnly: true, // Make the field readonly
                  }}
                />
              </Grid>
              {/* Payment Date Field */}
              <Grid item xs={4}>
                <TextField
                  label="Payment Date"
                  variant="outlined"
                  fullWidth
                  value={paymentDate}
                  onChange={handlePaymentDateChange}
                  error={paymentDateError}
                  helperText={paymentDateError ? "Payment Date is required" : ""}
                  required
                  type="date"
                  InputLabelProps={{
                    shrink: true, // Ensure label stays above the input
                  }}
                />
              </Grid>
              {/* Payment Method Field */}
              <Grid item xs={4}>
                <TextField
                  label="Payment Method"
                  variant="outlined"
                  fullWidth
                  value={paymentMethod}
                  onChange={handlePaymentMethodChange}
                  error={paymentMethodError}
                  helperText={paymentMethodError ? "Payment Method is required" : ""}
                  required
                  select
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 44.13, // Set consistent height for the select field
                    },
                  }}
                >
                  {paymentMethodOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2}>
              {/* Paid By Field */}
              <Grid item xs={6}>
                <TextField
                  label="Paid By"
                  variant="outlined"
                  fullWidth
                  value={paidBy}
                  onChange={handlePaidByChange}
                  error={paidByError}
                  helperText={paidByError ? "Paid By is required" : ""}
                  required
                />
              </Grid>
              {/* Cheque Number Field */}
              {paymentMethod === "79039c7b-bb79-4876-907b-e9a496a06426" && (
                <Grid item xs={6}>
                  <TextField
                    label="Cheque Number"
                    variant="outlined"
                    fullWidth
                    value={chequeNumber}
                    onChange={handleChequeNumberChange}
                    error={chequeNumberError}
                    helperText={chequeNumberError ? "Cheque Number is required" : ""}
                    required
                  />
                </Grid>
              )}
            </Grid>
            {/* Extra Notes Field */}
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12}>
                <TextField
                  label="Extra Notes"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={extraNotes} // Bind state to the field
                  onChange={handleExtraNotesChange} // Handle changes
                  placeholder="Add any additional notes here (optional)"
                />
              </Grid>
            </Grid>
            {/* Payment Document Upload Field */}
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12}>
                <TextField
                  type="file"
                  variant="outlined"
                  fullWidth
                  onChange={handlePaymentDocumentChange}
                  error={paymentDocumentError}
                  helperText={
                    paymentDocumentError
                      ? `Invalid file. Allowed types: PDF, Excel, DOCX, JPEG, PNG. Max size: ${MAX_FILE_SIZE_MB}MB`
                      : "Upload Payment Document (optional)"
                  }
                  InputLabelProps={{
                    shrink: true, // Ensure label stays above the input
                  }}
                />
              </Grid>
            </Grid>
          </MDBox>
        )}

        <AddNewPaymentForm onHandleSubmit={onHandleSubmit} />
      </DialogContent>
      <MDBox mt={3} display="flex" justifyContent="flex-end" px={3} pb={3}>
        <MDButton
          variant="contained"
          color="success"
          onClick={onHandleSubmit}
          disabled={loadingButton}
        >
          {loadingButton ? <CircularProgress size={24} color="inherit" /> : "Submit Payment"}
        </MDButton>
      </MDBox>
      {successMessage && <Alert severity="success">{successMessage}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
    </Dialog>
  );
}

export default AddNewPayment;
